import { gbp, localizeText } from "@/lib/format";
import { ArrowRight, ExternalLink, Info, X } from "lucide-react";

const TYPE_LABEL = { subscription: "Subscription", current_account: "Current account", savings: "Savings", credit_card: "Credit card" };

export default function DealCard({ deal, onDismiss }) {
  const slug = deal.id.replace(/[^a-z0-9]+/gi, "-");
  const sub = deal.type === "subscription";

  return (
    <div data-testid={`deal-card-${slug}`} className="rounded-xl border border-white/8 bg-[#0D0D0D] p-4 flex flex-col hover:border-[#00C896]/30 transition-colors">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] uppercase tracking-[0.14em] text-slate-500">{TYPE_LABEL[deal.type]}</span>
        <button data-testid={`deal-dismiss-${slug}`} onClick={() => onDismiss(deal)} className="inline-flex items-center gap-1 text-[11px] text-slate-500 hover:text-white transition-colors">
          <X className="h-3 w-3" /> Not interested
        </button>
      </div>

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] items-stretch gap-2">
        <div className="rounded-lg border border-white/6 bg-black/30 p-3 min-w-0">
          <p className="text-[10px] text-slate-500">You have</p>
          <p className="text-sm font-semibold mt-0.5 line-clamp-2">{deal.current.name}</p>
          {sub && <p className="font-num text-xs text-slate-300 mt-0.5">{gbp(deal.current.monthly_cost)}/mo</p>}
        </div>
        <ArrowRight className="h-4 w-4 text-[#00C896] self-center justify-self-center rotate-90 sm:rotate-0" />
        <div className="rounded-lg border border-[#00C896]/20 bg-[#00C896]/[0.06] p-3 min-w-0">
          <p className="text-[10px] text-[#4DE3BF]">Try instead</p>
          <p data-testid={`deal-alt-${slug}`} className="text-sm font-semibold mt-0.5 line-clamp-2">{deal.alternative.name}</p>
          {sub && <p className="font-num text-xs text-[#4DE3BF] mt-0.5">{gbp(deal.alternative.monthly_cost)}/mo</p>}
        </div>
      </div>

      <p className="text-[11px] text-slate-500 mt-2.5">{localizeText(deal.current.detail)}</p>
      <p data-testid={`deal-reason-${slug}`} className="text-sm text-slate-300 leading-relaxed mt-2 flex-1">{localizeText(deal.reason)}</p>

      {deal.disclaimer && (
        <p data-testid={`deal-disclaimer-${slug}`} className="mt-3 flex gap-1.5 rounded-lg border border-amber-500/20 bg-amber-500/[0.06] px-2.5 py-2 text-[11px] text-amber-200/90 leading-snug">
          <Info className="h-3.5 w-3.5 shrink-0 mt-px" /> {deal.disclaimer}
        </p>
      )}

      <div className="mt-3 flex items-center justify-between gap-3">
        <div>
          <p className="text-[10px] text-slate-500">{sub ? "You could save" : "Estimated gain"}</p>
          <p data-testid={`deal-saving-${slug}`} className="font-num text-base font-bold text-[#00C896]">
            {sub ? `${gbp(deal.monthly_saving)}/mo` : `${gbp(deal.yearly_gain)}/yr`}
            <span className="block sm:inline text-[11px] font-normal text-slate-500"><span className="hidden sm:inline"> · </span>{sub ? `${gbp(deal.yearly_gain)}/yr` : `${gbp(deal.monthly_saving)}/mo`}</span>
          </p>
        </div>
        <a data-testid={`deal-learn-more-${slug}`} href={deal.alternative.url} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-full bg-[#00C896] hover:bg-[#00E5AC] px-3.5 py-1.5 text-xs font-semibold text-[#04140D] transition-colors">
          Learn more <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </div>
  );
}
